import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable confirmation dialog for a mutating subscription action.
 *
 * Cancel-subscription and cancel-scheduled-change flows plug into this
 * so the pattern (title + banner + inline error + primary/secondary
 * buttons + duplicate-click protection) stays consistent across the
 * Billing page.
 */
export default function SubscriptionActionDialog({
  open,
  onOpenChange,
  title,
  description,
  effectiveMessage,
  destructive = false,
  primaryLabel,
  primaryTestid,
  endpoint,
  method = 'post',
  onSuccess,
}) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await api[method](endpoint);
      toast.success(title.replace(/\?$/, '') + ' — done');
      onOpenChange(false);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Could not complete this action. Please try again.';
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="subscription-action-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900">
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {description}
          </DialogDescription>
        </DialogHeader>
        {effectiveMessage && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700" data-testid="action-effective-banner">
            {effectiveMessage}
          </div>
        )}
        {error && (
          <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="action-error">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="action-cancel-btn"
          >
            Keep as-is
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy}
            className={
              destructive
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-[#4338CA] hover:bg-[#3730A3] text-white'
            }
            data-testid={primaryTestid}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : primaryLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
